import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { ShoppingCart, ExternalLink } from 'lucide-react';
import { useTranslation } from '@/components/utils/translations';

export default function LiveDealsCard({ deals = [], accountId }) {
  const { t } = useTranslation();
  const liveDeals = (deals || []).filter(p => p.status === 'Live');
  const liveDealsCount = liveDeals.length;

  // [2026-10-20] The tile used to render a COUNT ONLY, and it was clickable solely when
  // liveDealsCount === 1 (`const singleDeal = liveDealsCount === 1 ? liveDeals[0] : null`).
  // So an account with two or more live tenders showed a bare number with no way to reach
  // any of them — Hopwood Hall College has two (the CA18496 humanoid-robot market
  // engagement and an HR & Payroll system) and offered no link at all. Now every deal is
  // listed with its own source_url and its category. Each row is its own anchor, so the
  // whole-card wrapper link is no longer needed.
  const MAX_SHOWN = 3;
  const shown = liveDeals.slice(0, MAX_SHOWN);
  const hidden = liveDealsCount - shown.length;

  return (
    <Card className="text-center border-0 shadow-md hover:shadow-lg transition-shadow h-full">
      <CardContent className="p-3 md:p-4 flex flex-col items-center h-full">
        <p className="text-xs md:text-sm text-[#5C6B7A]">{t('liveDeals')}</p>
        <div className="flex items-center justify-center gap-1 my-1">
          <ShoppingCart className="w-4 h-4 text-[#00C2C2]" />
          <p className="text-lg md:text-2xl font-bold text-[#0A1A2F]">
            {liveDealsCount > 0 ? liveDealsCount : t('notApplicable')}
          </p>
        </div>

        {liveDealsCount === 0 ? (
          <div className="h-[16px] md:h-[20px]" />
        ) : (
          <div className="w-full mt-1 space-y-1 text-left">
            {shown.map((d, i) => {
              const title = d.title || d.product_name || d.source_award_id || '—';
              const category = d.category;
              const href = d.source_url || d.contract_link;
              const body = (
                <>
                  {category && (
                    <span className="block text-[10px] leading-tight text-[#00C2C2] truncate">
                      {category}
                    </span>
                  )}
                  <span className="flex items-start gap-1 text-[11px] leading-tight text-[#0A1A2F]">
                    <span className="line-clamp-2">{title}</span>
                    {href && <ExternalLink className="w-3 h-3 shrink-0 mt-[1px] text-[#5C6B7A]" />}
                  </span>
                </>
              );
              return href ? (
                <a
                  key={d.id || d.deal_id || i}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={title}
                  className="block rounded px-1 py-[2px] hover:bg-[#00C2C2]/10"
                >
                  {body}
                </a>
              ) : (
                <div key={d.id || d.deal_id || i} title={title} className="px-1 py-[2px]">
                  {body}
                </div>
              );
            })}
            {hidden > 0 && (
              <p className="px-1 text-[10px] text-[#5C6B7A]">+{hidden} {t('more')}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
